import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { TeamInfo, TeamInfoResponse, TeamModeValue } from '@shared/types';
import * as teamApi from '@/api/team';

function defaultState(): TeamInfoResponse {
  return {
    teamInfo: null,
    hasTeam: false,
    useTeamMode: 0,
    isTeamMode: false,
    teamId: 0,
    personalPoints: -1,
    teamPoints: -1,
    personalIsVip: false,
    teamIsVip: false,
  };
}

export const useTeamStore = defineStore('team', () => {
  const state = ref<TeamInfoResponse>(defaultState());
  const loading = ref(false);
  const switching = ref(false);
  const initialized = ref(false);
  const error = ref<string | null>(null);

  const teamInfo = computed<TeamInfo | null>(() => state.value.teamInfo);
  const hasTeam = computed(() => state.value.hasTeam);
  const useTeamMode = computed(() => state.value.useTeamMode);
  const isTeamMode = computed(() => state.value.isTeamMode);
  const teamPoints = computed(() => state.value.teamPoints);
  const teamIsVip = computed(() => state.value.teamIsVip);

  function applyTeamInfo(payload: TeamInfoResponse) {
    const hasTeamValue = Boolean(payload.hasTeam || payload.teamInfo?.id);
    const useTeamModeValue = payload.useTeamMode === 1 ? 1 : 0;
    const activeTeamId = payload.isTeamMode && useTeamModeValue === 1 && payload.teamId > 0
      ? payload.teamId
      : 0;

    state.value = {
      ...defaultState(),
      ...payload,
      hasTeam: hasTeamValue,
      teamInfo: payload.teamInfo ?? null,
      useTeamMode: useTeamModeValue,
      isTeamMode: hasTeamValue && activeTeamId > 0,
      teamId: activeTeamId,
    };
    initialized.value = true;
    error.value = null;
  }

  function clear() {
    state.value = defaultState();
    loading.value = false;
    switching.value = false;
    initialized.value = false;
    error.value = null;
  }

  async function fetchTeamInfo() {
    if (loading.value) return state.value;
    loading.value = true;
    try {
      const payload = await teamApi.getTeamInfo();
      applyTeamInfo(payload);
      return payload;
    } catch (err) {
      error.value = err instanceof Error ? err.message : '获取团队信息失败';
      initialized.value = true;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function switchMode(useTeamModeValue?: TeamModeValue) {
    if (!hasTeam.value || switching.value) {
      return state.value;
    }

    const nextMode = useTeamModeValue ?? (isTeamMode.value ? 0 : 1);
    switching.value = true;
    try {
      const payload = await teamApi.switchTeamMode(nextMode);
      applyTeamInfo(payload);
      return payload;
    } catch (err) {
      error.value = err instanceof Error ? err.message : '切换团队身份失败';
      throw err;
    } finally {
      switching.value = false;
    }
  }

  return {
    state,
    loading,
    switching,
    initialized,
    error,
    teamInfo,
    hasTeam,
    useTeamMode,
    isTeamMode,
    teamPoints,
    teamIsVip,
    fetchTeamInfo,
    switchMode,
    clear,
  };
});
